import React, { useRef, useState } from "react";
import axios from "axios";
import { Video, X, Upload, Loader2, Play, FileVideo } from "lucide-react";
import { resolveImageUrl } from "./ImageUpload";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Chunk size on the wire — R2 multipart minimum for non-last part is 5 MB. */
const CHUNK_SIZE = 10 * 1024 * 1024;
const HARD_LIMIT = 1024 * 1024 * 1024; // 1 ГБ — прямая загрузка в R2 позволяет не жать видео
const ALLOWED_EXT = ["mp4", "mov", "webm", "m4v"];
const PARALLEL_UPLOADS = 3;           // number of chunks in flight simultaneously

/**
 * Video uploader for the product editor.
 *
 * v2 pipeline (direct-to-R2 multipart, survives backend redeploys):
 *   1. init-r2 → backend mints R2 multipart upload_id + presigned PUT URL per part
 *   2. PUT each chunk directly to R2 (3 in parallel) — nothing touches our backend
 *   3. capture poster frame via <video> + canvas
 *   4. upload poster as a normal image
 *   5. complete-r2 → backend tells R2 to assemble; returns final URL
 *
 * If the backend restarts mid-flight, R2 still remembers the multipart and the
 * pending chunks keep uploading. Only the final "complete" call has to succeed.
 */
export default function VideoUpload({ value = [], onChange, maxVideos = 3, testId = "video-upload", label = "Видео товара", hint = "" }) {
  const fileRef = useRef(null);
  const cancelRef = useRef(null);
  const [progress, setProgress] = useState(null); // {sent, total, phase}
  const [error, setError] = useState("");

  const items = Array.isArray(value) ? value : [];

  const formatSize = (n) => {
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} КБ`;
    return `${(n / (1024 * 1024)).toFixed(1)} МБ`;
  };
  const formatDuration = (s) => {
    if (!s) return "";
    const m = Math.floor(s / 60);
    const r = Math.round(s % 60);
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      setError(`Формат не поддерживается. Загрузите: ${ALLOWED_EXT.join(", ").toUpperCase()}`);
      return;
    }
    if (file.size > HARD_LIMIT) {
      setError(`Файл больше 1 ГБ (${formatSize(file.size)}). Сожмите видео.`);
      return;
    }
    if (items.length >= maxVideos) {
      setError(`Максимум ${maxVideos} видео на товар`);
      return;
    }

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const ctrl = new AbortController();
    cancelRef.current = ctrl;
    let initState = null; // {key, upload_id, part_urls}

    try {
      // 1. Ask backend to reserve a multipart upload
      setProgress({ sent: 0, total: file.size, phase: "init" });
      const initRes = await axios.post(`${API}/upload/video/init-r2`, {
        filename: file.name,
        total_size: file.size,
        total_chunks: totalChunks,
      }, { withCredentials: true, signal: ctrl.signal });
      initState = initRes.data;

      // 2. Upload chunks directly to R2 (parallel workers)
      const uploaded = new Array(totalChunks).fill(null);   // {PartNumber, ETag}
      const sizes = new Array(totalChunks).fill(0);
      let sentBytes = 0;
      let nextIdx = 0;

      const bumpProgress = (i, delta) => {
        sizes[i] += delta;
        sentBytes = sizes.reduce((a, b) => a + b, 0);
        setProgress({ sent: Math.min(sentBytes, file.size), total: file.size, phase: "uploading" });
      };

      const uploadOne = async (i) => {
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        const url = initState.part_urls[i].url;
        const resp = await axios.put(url, blob, {
          signal: ctrl.signal,
          headers: { "Content-Type": "application/octet-stream" },
          transformRequest: [(d) => d], // don't let axios mangle Blob
          onUploadProgress: (evt) => {
            if (!evt.loaded) return;
            const already = sizes[i];
            const now = Math.min(evt.loaded, blob.size);
            bumpProgress(i, now - already);
          },
        });
        // R2 returns the part ETag in the response headers
        const etag = resp.headers?.etag || resp.headers?.ETag;
        if (!etag) throw new Error(`R2 не вернул ETag для части ${i + 1}`);
        uploaded[i] = { PartNumber: initState.part_urls[i].part_number, ETag: etag };
        sizes[i] = blob.size;
        bumpProgress(i, 0);
      };

      const worker = async () => {
        while (true) {
          const i = nextIdx++;
          if (i >= totalChunks) return;
          await uploadOne(i);
        }
      };
      await Promise.all(Array.from({ length: Math.min(PARALLEL_UPLOADS, totalChunks) }, worker));

      // 3. Capture poster frame + measure duration client-side
      setProgress({ sent: file.size, total: file.size, phase: "poster" });
      let posterUrl = null;
      let duration = 0;
      try {
        const posterRes = await capturePosterAndDuration(file);
        duration = posterRes.duration;
        if (posterRes.blob) {
          // Upload the poster JPEG via the normal image endpoint
          const fd = new FormData();
          fd.append("file", posterRes.blob, "poster.jpg");
          const posterUp = await axios.post(`${API}/upload/file`, fd, {
            withCredentials: true, signal: ctrl.signal,
          });
          posterUrl = posterUp.data?.url || null;
        }
      } catch (posterErr) {
        // Poster failure is non-fatal — the video still uploads
        console.warn("Poster generation failed:", posterErr);
      }

      // 4. Ask backend to complete the multipart upload
      setProgress({ sent: file.size, total: file.size, phase: "processing" });
      const complete = await axios.post(`${API}/upload/video/complete-r2`, {
        key: initState.key,
        upload_id: initState.upload_id,
        parts: uploaded,
        poster_url: posterUrl,
        duration,
        size: file.size,
        original_name: file.name,
      }, { withCredentials: true, signal: ctrl.signal });

      const v = complete.data;
      onChange([...items, {
        url: v.url, poster: v.poster, duration: v.duration,
        size: v.size, original_name: file.name,
      }]);
    } catch (err) {
      if (err.name === "CanceledError") {
        setError("Загрузка отменена");
      } else {
        setError(err.response?.data?.detail || err.message || "Ошибка загрузки видео");
      }
      // Best-effort: tell R2 to release the multipart parts we already uploaded
      if (initState) {
        try {
          await axios.post(`${API}/upload/video/abort-r2`, {
            key: initState.key, upload_id: initState.upload_id,
          }, { withCredentials: true });
        } catch { /* ignore */ }
      }
    } finally {
      setProgress(null);
      cancelRef.current = null;
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const cancel = () => {
    if (cancelRef.current) cancelRef.current.abort();
  };

  const uploading = !!progress;
  const pct = uploading ? Math.min(100, Math.round((progress.sent / progress.total) * 100)) : 0;
  const phaseLabel = progress?.phase === "poster" ? "Готовим превью…"
                    : progress?.phase === "processing" ? "Собираем в облаке…"
                    : progress?.phase === "init" ? "Инициализация…"
                    : `Загрузка ${formatSize(progress?.sent || 0)} / ${formatSize(progress?.total || 0)}`;

  return (
    <div data-testid={testId}>
      <label style={lbl}>
        {label} <span style={lblHint}>({items.length}/{maxVideos}, до 1 ГБ)</span>
      </label>
      {hint && <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#8a8a89", margin: "-2px 0 8px" }}>{hint}</div>}

      {/* Existing videos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: "10px", marginBottom: "12px" }}>
        {items.map((v, idx) => (
          <div key={(v.url || "") + idx} data-testid={`${testId}-item-${idx}`}
               style={{ position: "relative", aspectRatio: "1/1", borderRadius: "6px",
                        overflow: "hidden", border: "1px solid #e5e7eb", background: "#0A0A0A" }}>
            {v.poster ? (
              <img src={resolveImageUrl(v.poster)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }} />
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#666" }}>
                <FileVideo size={28} />
              </div>
            )}
            {/* Play overlay */}
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                          pointerEvents: "none" }}>
              <div style={{ background: "rgba(0,0,0,0.55)", borderRadius: "50%", width: "44px", height: "44px",
                            display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Play size={20} color="#fff" fill="#fff" />
              </div>
            </div>
            {/* Duration pill */}
            {v.duration ? (
              <div style={{ position: "absolute", bottom: "6px", right: "6px",
                            background: "rgba(0,0,0,0.7)", color: "#fff",
                            fontSize: "10px", padding: "2px 6px", borderRadius: "3px",
                            fontFamily: "Manrope", fontWeight: 600 }}>
                {formatDuration(v.duration)}
              </div>
            ) : null}
            <button type="button" onClick={() => remove(idx)}
                    data-testid={`${testId}-remove-${idx}`}
                    style={removeBtn}>
              <X size={12} />
            </button>
          </div>
        ))}

        {items.length < maxVideos && !uploading && (
          <button type="button" onClick={() => fileRef.current?.click()}
                  data-testid={`${testId}-pick-btn`} style={pickBtn}>
            <Upload size={18} />
            <span style={{ fontSize: "11px" }}>Загрузить видео</span>
            <span style={{ fontSize: "10px", color: "#bbb" }}>MP4 · MOV · WebM</span>
          </button>
        )}
      </div>

      {/* Progress bar */}
      {uploading && (
        <div data-testid={`${testId}-progress`}
             style={{ background: "#fafafa", border: "1px solid #e5e7eb",
                      borderRadius: "6px", padding: "10px 14px", marginBottom: "10px" }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                         fontFamily: "Manrope", fontSize: "12px", marginBottom: "6px" }}>
            <span style={{ color: "#0A0A0A", fontWeight: 600 }}>
              {progress.phase === "processing" || progress.phase === "poster" || progress.phase === "init" ? (
                <><Loader2 size={12} className="spin" style={{ display: "inline", animation: "spin 1s linear infinite", marginRight: "4px", verticalAlign: "middle" }} />
                  {phaseLabel}</>
              ) : (
                phaseLabel
              )}
            </span>
            <button type="button" onClick={cancel} data-testid={`${testId}-cancel-btn`}
                    style={{ background: "none", border: "none", color: "#dc2626",
                             cursor: "pointer", fontFamily: "Manrope", fontSize: "12px" }}>
              Отмена
            </button>
          </div>
          <div style={{ height: "6px", background: "#e5e7eb", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%",
                          background: "linear-gradient(90deg,#C5A059,#D4B878)",
                          transition: "width 0.3s" }} />
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm"
             onChange={handlePick} style={{ display: "none" }}
             data-testid={`${testId}-file-input`} />

      {error && (
        <div data-testid={`${testId}-error`}
             style={{ color: "#dc2626", fontSize: "12px", marginTop: "6px", fontFamily: "Manrope" }}>
          {error}
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const lbl = {
  fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280",
  letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px", display: "block",
};
const lblHint = { color: "#9ca3af", fontWeight: 500, letterSpacing: 0, textTransform: "none" };
const pickBtn = {
  aspectRatio: "1/1", borderRadius: "6px", border: "1.5px dashed #d1d5db",
  background: "#fafafa", cursor: "pointer", display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center", gap: "6px", color: "#9ca3af",
  fontFamily: "Manrope", fontSize: "11px",
};
const removeBtn = {
  position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.75)",
  border: "none", borderRadius: "50%", width: "22px", height: "22px",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", color: "white", zIndex: 2,
};

VideoUpload.Video = Video;

/**
 * Client-side poster extraction: draws a frame at t=1s (or midway for very
 * short clips) into a canvas, returns a JPEG blob + duration.
 * Backend never has to download the 100 MB video just for a thumbnail.
 */
async function capturePosterAndDuration(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;

    // Wait for metadata to load so we know duration + dimensions
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Не удалось прочитать метаданные видео"));
      setTimeout(() => reject(new Error("Таймаут чтения видео")), 8000);
    });
    const duration = video.duration || 0;

    // Seek to a sensible frame — 1 s in, or midpoint for very short clips
    const seekTo = duration > 0 ? Math.min(1.0, Math.max(0.1, duration / 2)) : 0.5;
    await new Promise((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error("Ошибка перемотки"));
      try { video.currentTime = seekTo; } catch (e) { reject(e); }
      setTimeout(() => reject(new Error("Таймаут перемотки")), 4000);
    });

    // Downscale to width 720 to keep the poster small
    const maxW = 720;
    const scale = video.videoWidth > maxW ? maxW / video.videoWidth : 1;
    const w = Math.max(1, Math.round(video.videoWidth * scale));
    const h = Math.max(1, Math.round(video.videoHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85)
    );
    return { blob, duration: Math.round(duration * 10) / 10 };
  } finally {
    URL.revokeObjectURL(url);
  }
}
